'use client';

import { createClient } from '@/lib/supabase/client';

export type Stream = 'Engineering' | 'Medical' | 'Management' | 'Law' | 'Arts' | 'Commerce' | 'Science';

export interface ExamPattern {
  section: string;
  questions: number;
  marks: number;
  duration: string;
  negativeMarking: string;
}

export interface ImportantDate {
  event: string;
  date: string;
  status: 'completed' | 'upcoming' | 'ongoing';
}

export interface CollegeCutoff {
  collegeId: string;
  collegeName: string;
  location: string;
  nirfRank: number | null;
  general: number;
  obc: number;
  sc: number;
  st: number;
  ews: number;
  unit: string;
  year: number;
}

export interface SyllabusSection {
  subject: string;
  topics: string[];
}

export interface Exam {
  id: string;
  name: string;
  shortName: string;
  conductingBody: string;
  stream: Stream[];
  level: 'National' | 'State' | 'University';
  mode: 'Online' | 'Offline' | 'Both';
  examDate: string;
  resultDate: string;
  registrationStart: string;
  registrationEnd: string;
  applicationFee: number;
  totalSeats: number;
  totalApplicants: number;
  description: string;
  eligibility: string;
  frequency: string;
  officialWebsite: string;
  colorClass: string;
  pattern: ExamPattern[];
  importantDates: ImportantDate[];
  collegeCutoffs: CollegeCutoff[];
  syllabusSections: SyllabusSection[];
}

function mapExam(row: any, cutoffRows: any[]): Exam {
  const collegeCutoffs: CollegeCutoff[] = cutoffRows
    .filter((c) => c.exam_id === row.id)
    .map((c) => ({
      collegeId: c.college_id,
      collegeName: c.colleges?.name || '',
      location: c.colleges?.location || '',
      nirfRank: c.nirf_rank,
      general: Number(c.general),
      obc: Number(c.obc),
      sc: Number(c.sc),
      st: Number(c.st),
      ews: Number(c.ews),
      unit: c.unit,
      year: c.cutoff_year,
    }));

  return {
    id: row.id,
    name: row.name,
    shortName: row.short_name,
    conductingBody: row.conducting_body,
    stream: (row.streams || []) as Stream[],
    level: row.exam_level as Exam['level'],
    mode: row.exam_mode as Exam['mode'],
    examDate: row.exam_date,
    resultDate: row.result_date,
    registrationStart: row.registration_start,
    registrationEnd: row.registration_end,
    applicationFee: row.application_fee,
    totalSeats: row.total_seats,
    totalApplicants: row.total_applicants,
    description: row.description,
    eligibility: row.eligibility,
    frequency: row.frequency,
    officialWebsite: row.official_website,
    colorClass: row.color_class,
    pattern: Array.isArray(row.pattern) ? row.pattern : [],
    importantDates: Array.isArray(row.important_dates) ? row.important_dates : [],
    collegeCutoffs,
    syllabusSections: Array.isArray(row.syllabus_sections) ? row.syllabus_sections : [],
  };
}

export async function fetchExams(): Promise<Exam[]> {
  const supabase = createClient();
  try {
    const results = await Promise.all([
      supabase.from('exams').select('*').order('name', { ascending: true }),
      supabase.from('cutoffs').select('*, colleges(name, location)').not('exam_id', 'is', null),
    ]);

    const { data: examRows, error: eErr } = results[0];
    const { data: cutoffRows, error: cErr } = results[1];

    if (eErr || cErr) {
      console.error('fetchExams error:', eErr?.message || cErr?.message);
      return [];
    }

    return (examRows || []).map((row) => mapExam(row, cutoffRows || []));
  } catch (err: any) {
    console.error('fetchExams exception:', err.message);
    return [];
  }
}
